import { stdnum } from "stdnum";

import { CountryCode } from "@krea/shared/types/common";

const convertTo12DigitSSN = (ssn: string) => {
  // Extract the year, month, and day from the SSN
  const [datePart, serialPart] = ssn.split("-"); // Example: 'YYMMDD' and 'XXXX'
  if (!datePart || !serialPart) {
    throw new Error("Invalid SSN format");
  }

  const year = Number.parseInt(datePart.slice(0, 2), 10); // Extract the year (YY)
  const currentYear = new Date().getFullYear(); // Get current year, e.g., 2024
  const currentCentury = Math.floor(currentYear / 100); // E.g., 20

  // Deduce the full year (YYYY)
  let fullYear: number;

  // Current two-digit year to compare with
  const currentTwoDigitYear = currentYear % 100;

  if (year > currentTwoDigitYear) {
    // If year is greater than current two-digit year, assume it's from the previous century
    fullYear = (currentCentury - 1) * 100 + year; // 1900s
  } else {
    // Otherwise, assume it's from the current century
    fullYear = currentCentury * 100 + year; // 2000s
  }

  // Return the 12-digit SSN
  return `${fullYear}${datePart.slice(2)}-${serialPart}`;
};

interface ValidatorReturnType {
  formatted: string; // Formatted ("-" etc)
  compact: string; // Cleaned from any formatting ("-" etc)
  isValid: boolean;
}

const SESsnValidator = (ssn: string): ValidatorReturnType => {
  if (!stdnum[CountryCode.SE]?.personnummer) {
    throw new Error("Swedish SSN validator not available");
  }
  const stdNumSESsn = stdnum[CountryCode.SE].personnummer;

  // always needs a formated SSN to validate
  const isValid =
    ssn.length > 0
      ? stdNumSESsn.validate(stdNumSESsn.format(ssn)).isValid
      : false;
  let formatted = ssn;

  if (isValid) {
    formatted = stdNumSESsn.format(ssn);

    // Currently the valid formatted always returns the 10 digit format (plus the dash)
    // But we always want to work with the 12 digit format
    if (formatted.length === 11) {
      formatted = convertTo12DigitSSN(formatted);
    }
  }
  const compact = formatted.replace(/-/g, "");

  return {
    formatted,
    compact,
    isValid,
  };
};

export const ssnValidator = (
  value: string,
  countryCode: CountryCode,
): ValidatorReturnType => {
  switch (countryCode) {
    case CountryCode.SE:
      return SESsnValidator(value);
    case CountryCode.FI:
      if (!stdnum[countryCode]?.hetu) {
        throw new Error("Finnish SSN validator not available");
      }
      return {
        formatted: stdnum[countryCode].hetu.format(value),
        compact: stdnum[countryCode].hetu.compact(value),
        isValid: stdnum[countryCode].hetu.validate(value).isValid,
      };
    default:
      throw new Error(`Country code [${countryCode}] not supported`);
  }
};
